<template>
  <m-form-row data-cy="packs-interactive-list--form-row">
    <div>
      <div v-if="!arePacksAvailable && !isLoadingPacks" class="py-2 text-error" data-cy="packs-interactive-list-empty">
        {{ $t('offer-manager.create.step-complements.packs.no-packs-available') }}
      </div>
      <m-interactive-list
        data-cy="packs-interactive-list"
        v-model="selectedPacks"
        :addButtonText="$t('offer-manager.create.step-complements.packs.add-button-text')"
        :emptyListText="$t('offer-manager.create.step-complements.packs.empty-list-text')"
        @addNewItem="value => commit('ADD_PACK', value)"
        :disabled="!arePacksAvailable || disabled"
        :max="15 - complementsList.length"
        :loading="isLoadingPacks"
        :key="refreshKey">
        <template #list-item="{item, index}">
          <div class="py-1 d-flex" :key="item.partNo">
            <m-select
              :key="`pack${index}partNo${refreshKey}`"
              :data-cy="`pack${index}partNo`"
              :value="item.partNo"
              :items="item.removed ? availablePacks : selectablePacks"
              class="col-4 pl-0 py-0"
              :placeholder="$t('offer-manager.create.step-complements.packs.select-pack-placeholder')"
              itemText="description"
              itemTextCaption="partNoPortal"
              itemValue="partNo"
              :returnObject="true"
              @input="
                pack => {
                  commit('SET_PACK', {pack, index})
                }
              "
              :disabled="!arePacksAvailable || item.removed || disabled">
              <template v-slot:item="{item}">
                <span>{{ item.description }}</span>
                <span>{{ item.partNo }}</span>
              </template>
            </m-select>
            <price-field :data-cy="`pack${index}price`" class="col-3 pl-0 py-0" :value="item.price" readonly />
            <discount-field
              :uid="`pack${index}discount`"
              class="col-3 pl-0 py-0"
              :valueCurrency="item.discount"
              :valuePercentage="item.discountPercent"
              :placeholder="$t('offer-manager.create.step-vehicle-registration.lbl-discount-placeholder')"
              @changeValueCurrency="discount => commit('SET_PACK_DISCOUNT', {discount: parseFloat(discount), index})"
              @changeValuePercentage="
                discount => commit('SET_PACK_DISCOUNT_PERCENT', {discount: parseFloat(discount), index})
              "
              :disabled="!item.price || !arePacksAvailable || item.removed || disabled"
              :rulesPercentage="complementDiscountRulesPercentage"
              :rulesCurrency="complementDiscountRules"
              :maxCurrency="item.price"
              @validateForm="validateForm" />
            <price-field
              :data-cy="`pack${index}total`"
              class="col-2 p-0"
              :key="item.getTotalPrice()"
              :value="item.getTotalPrice()"
              readonly />
          </div>
        </template>
      </m-interactive-list>
    </div>
  </m-form-row>
</template>

<script lang="ts">
import {computed, defineComponent, ref, watch} from '@vue/composition-api'

import DiscountField from '@/components/general/DiscountField.vue'
import PriceField from '@/components/general/PriceField.vue'
// @ts-ignore
import {complementDiscountRules, complementDiscountRulesPercentage} from '@/helpers/offer-manager-rules'
import {useI18n} from '@/i18n'
import {CustomPacks, PackItem} from '@/types'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'Packs',
  components: {DiscountField, PriceField},
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const {t} = useI18n()
    const refreshKey = ref(0)

    const {
      getComplements: complementsList,
      getVehicleModelPacks: availablePacks,
      isLoadingPacks,
      getPacks: packs,
    } = mapGetters(root.$store.getters)

    const selectablePacks = computed(() => {
      const filteredPacks = availablePacks.value.filter((item: PackItem) => !item.removed)

      return props.isEditing && filteredPacks.length === 0 && availablePacks.value && availablePacks.value.length === 0
        ? selectedPacks.value
        : filteredPacks
    })

    const arePacksAvailable = computed(() => {
      return availablePacks.value.length > 0
    })

    const selectedPacks = computed({
      get: (): CustomPacks | undefined => packs.value,
      set: (value: CustomPacks | undefined) => root.$store.commit('SET_PACKS', value),
    })

    watch(arePacksAvailable, () => {
      refreshKey.value++
    })

    function validateForm() {
      emit('validateForm')
    }

    return {
      t,
      refreshKey,
      complementDiscountRules,
      complementDiscountRulesPercentage,
      complementsList,
      selectablePacks,
      arePacksAvailable,
      availablePacks,
      isLoadingPacks,
      selectedPacks,
      packs,
      commit: root.$store.commit,
      validateForm,
    }
  },
})
</script>
